import { t } from './localizationController';
import {
  addV2,
  clamp,
  cloneV2,
  Ease,
  hasVisibleState,
  lerpNumber,
  lerpV2,
  remapNumber,
  subtractV2,
  toSize,
  v2,
} from './helpers';
import {
  addGameObject,
  anchoredPos,
  moveEntityToBack,
  getZeroStaticEntity,
  setParent,
  setEntityPosition,
  getAbsolutePos,
  getObjectCountByType,
  getFirstObjectByType,
  getZeroAnchoredEntity,
  createTransformAnchoredAnimation,
} from './entity';
import { createAndAddLocalContainer } from './container';
import {
  CANVAS_SPEED,
  ObjectType,
  ServerEvent_Canvas,
  ServerEvent_EntityProps,
  Side,
  type CanvasFocus,
} from './shared/api_shared_types';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import {
  AnimationType,
  Canvas,
  Entity,
  VisibilityState,
  GameManager,
  GameObject,
  GameObjectController,
  MoveMode,
  RenderItemType,
  Shape,
  SharedObjectType,
  SliderOrientation,
  V2,
  type TextObject2,
} from './types';
import { buttonStates, isArrowKeyDown } from './input';
import { dt } from './index';
import { BUTTON_STYLES, SHADOWS, SHAPE_STYLES } from './constants';
import { COLORS } from './constants-colors';
import { createAndAddSlider, setSliderValue } from './slider';
import { updateRoomCanvas } from './room';
import { createAndAddLocalIconButton } from './icon_button';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import { hideToolTip, showStickyToolTip } from './controllers/tooltip_controller';

const x0 = 0;
const x1 = 100;
const minZoomLevel = 0.2;
const maxZoomLevel = 8;

const ZOOM_CONSTANT_B = Math.log(maxZoomLevel / minZoomLevel) / x1 - x0;
const ZOOM_CONSTANT_A = minZoomLevel * Math.exp(-ZOOM_CONSTANT_B * x0);
const NORMALIZED_ZOOM_100 = getNormalizedZoomLevel(1);
export const NORMALIZED_ZOOMED_OUT = getNormalizedZoomLevel(minZoomLevel);

const SCALE_STEPS = [0.2, 0.3, 0.4, 0.5, 0.75, 1, 1.5, 2];

const FOCUS_CHECK_COOLDOWN = 0;

const defaultFontId = 'font_regular';

const DURATION_PER_SPEED = {
  instant: 0,
  fast: 0.4,
  default: 1,
  slow: 2,
};

export function controller(): GameObjectController {
  return {
    getNew: getZeroCanvas,
    sync,
    update,
    createPackage,
  };
}

export function getZeroCanvas(ent: Entity, props: Partial<Canvas> = {}): Canvas {
  const newCanvas: Canvas = {
    ...ent,
    objectType: SharedObjectType.CANVAS,
    centerAnchor: null,
    debugView: false,
    layer: null,
    centerContainer: null,
    debugFocusContainer: null,
    scheduleUpdateLayout: false,
    layerDragOffset: v2(),
    focusPosition: v2(),
    focusSize: toSize(0, 0),
    relativeScreenFocusPoint: v2(0.5, 0.5),
    allowScrollHor: false,
    allowScrollVert: false,
    allowZoom: false,
    autoSize: false,
    scrollAnimation: null,
    zoomAnimation: null,
    stickToDefaultFocus: false,
    listMode: false,

    doCheckFocus: false,
    isOutOfFocus: false,
    focusCheckCooldown: 0,
    canvasMovedThisFrame: false,
    refocus: null,
    showNavigationTip: false,
    showRefocusTip: false,

    minLayerWidth: 0,
    minLayerHeight: 0,
    normalizedZoomLevelTarget: 0,
    normalizedZoomLevel: 0,
    zoomSlider: null,
    showScrollbar: false,
    scrollSliderHor: null,
    layerBasePos: v2(),
    leftBound: 0,
    rightBound: 0,
    topBound: 0,
    noBorders: false,
    bottomBound: 0,
    focus: {
      positionX: 0,
      positionY: 0,
      width: 100,
      height: 100,
      speed: CANVAS_SPEED.DEFAULT,
    },
    requestApplyFocus: null,
    onScaleChange: null,
    linked: null,

    ...props,
  };

  return newCanvas;
}

function sync(manager: GameManager, canvas: Canvas, data: ServerEvent_Canvas) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (canvas.id) {
    if (canvas.lifeTime < 0.2) {
      // when lifetime is 0, this is still on init phase
      setAutoZoom(canvas);
    }
  }

  if (!canvas.id) {
    addGameObject(manager, canvas);

    canvas.allowScrollVert = true;
    canvas.allowScrollHor = true;
    canvas.allowZoom = true;
    canvas.autoSize = true;

    initCanvas(manager, canvas);

    const [room, ent] = setParentByEntityProps(manager, canvas, data.sId, data.roomId, entityProps);

    updateRoomCanvas(canvas);

    room.canvas = canvas;

    canvas.moveMode = MoveMode.ANCHORED;
    moveEntityToBack(canvas);
    canvas.fixedZIndex = true;
    canvas.anchoredPos = anchoredPos(v2(0, 0), v2(0, 0), v2(0, 0));
  }

  let updateFocus = false;

  if (data.hasOwnProperty('relativeScreenFocusPointX')) {
    if (canvas.relativeScreenFocusPoint.x !== data.relativeScreenFocusPointX) {
      canvas.relativeScreenFocusPoint.x = data.relativeScreenFocusPointX;
      updateFocus = true;
    }
  }
  if (data.hasOwnProperty('relativeScreenFocusPointY')) {
    if (canvas.relativeScreenFocusPoint.y !== data.relativeScreenFocusPointY) {
      canvas.relativeScreenFocusPoint.y = data.relativeScreenFocusPointY;
      updateFocus = true;
    }
  }

  if ('enableRefocus' in data) {
    canvas.doCheckFocus = data.enableRefocus;
  }

  if ('showNavigationTip' in data) {
    canvas.showNavigationTip = data.showNavigationTip;
  }

  if ('showRefocusTip' in data) {
    canvas.showRefocusTip = data.showRefocusTip;
  }

  if (updateFocus) {
    let fp = canvas.focusPosition;

    if (canvas.relativeScreenFocusPoint.x !== 0.5 || canvas.relativeScreenFocusPoint.y !== 0.5) {
      const pointX = canvas.relativeScreenFocusPoint.x;
      const pointY = canvas.relativeScreenFocusPoint.y;
      const xOffset = (1 - pointX - 0.5) * canvas.width;
      const yOffset = (1 - pointY - 0.5) * canvas.height;
      const scaledOffset = v2(xOffset / canvas.layer.scale, yOffset / canvas.layer.scale);

      fp = addV2(fp, scaledOffset);
    }

    const onInit = !canvas.id || canvas.lifeTime <= 0.2;
    const ignoreBorders = true;

    setCanvasFocusPositionAndScale(canvas, fp, onInit ? 0 : 1, ignoreBorders);
  }

  if (
    data.hasOwnProperty('refocusRequest') ||
    data.hasOwnProperty('focusX') ||
    data.hasOwnProperty('focusY') ||
    data.hasOwnProperty('focusW') ||
    data.hasOwnProperty('focusH')
  ) {
    setFocus(
      canvas,
      data.hasOwnProperty('focusX') ? data.focusX : canvas.focus.positionX,
      data.hasOwnProperty('focusY') ? data.focusY : canvas.focus.positionY,
      data.hasOwnProperty('focusW') ? data.focusW : canvas.focus.width,
      data.hasOwnProperty('focusH') ? data.focusH : canvas.focus.height,
      data.hasOwnProperty('focusSpeed') ? data.focusSpeed : canvas.focus.speed,
    );
  }

  // setGameObjectyEntityProps(canvas, entityProps);
}

export function createAndAddLocalCanvas(
  manager: GameManager,
  ent: Entity,
  props: Partial<Canvas>,
): Canvas {
  const canvas = getZeroCanvas(ent, props);
  addGameObject(manager, canvas);

  initCanvas(manager, canvas);

  return canvas;
}

export function initCanvas(manager: GameManager, canvas: Canvas) {
  canvas.shape = Shape.RECT;
  canvas.allowDrag = false;

  if (!canvas.minLayerWidth) {
    canvas.minLayerWidth = 0;
  }
  if (!canvas.minLayerHeight) {
    canvas.minLayerHeight = 0;
  }

  canvas.allowHighlight = true;
  canvas.clickable = true;
  canvas.attachChild = attachChild;

  canvas.centerAnchor = createAndAddLocalContainer(manager, getZeroStaticEntity());
  setVisibilityState(canvas.centerAnchor, VisibilityState.DEFAULT);
  canvas.centerAnchor.fixedZIndex = true;

  canvas.layer = createAndAddLocalContainer(manager, getZeroStaticEntity());
  canvas.layer.scale = 1;

  canvas.layer.width = 100;
  canvas.layer.height = 100;
  canvas.focusSize.width = canvas.width / canvas.layer.scale;
  canvas.focusSize.height = canvas.height / canvas.layer.scale;
  canvas.layer.fixedZIndex = true;
  canvas.layer.pushBackChildren = !canvas.autoSize;
  // canvas.layer.shapeStyle = SHAPE_STYLES.DEBUG;

  canvas.centerContainer = createAndAddLocalContainer(manager, getZeroStaticEntity());
  setParent(canvas.centerContainer, canvas);
  // canvas.centerContainer.shapeStyle = SHAPE_STYLES.DEBUG_RED;

  //set to true to draw the current focus area as debug information
  if (false) {
    canvas.debugFocusContainer = createAndAddLocalContainer(manager, getZeroStaticEntity());
    setParent(canvas.debugFocusContainer, canvas.layer, v2());
    canvas.debugFocusContainer.shapeStyle = SHAPE_STYLES.DEBUG_GREEN;
  }

  setVisibilityState(canvas.layer, VisibilityState.APPEARING);

  for (let i = canvas.children.length - 1; i >= 0; i--) {
    attachChild(canvas.children[i], canvas);
  }

  setParent(canvas.centerAnchor, canvas, v2());
  setParent(canvas.layer, canvas.centerAnchor, v2());

  setCanvasLayerSize(canvas, canvas.minLayerWidth, canvas.minLayerHeight);

  if (canvas.allowZoom) {
    setAutoZoom(canvas);
  } else {
    setNormalizedZoomLevel(canvas, NORMALIZED_ZOOM_100);
  }

  if (canvas.showScrollbar) {
    createScrollSliderForCanvas(canvas);

    canvas.scrollSliderHor.height = canvas.height;
  }

  setVisibilityState(canvas, VisibilityState.APPEARING);
}

function update(c: Canvas, parentSizeChanged: boolean): boolean {
  const m = c.manager;

  if (c.visibilityState === VisibilityState.HIDING) {
    c.opacity = 1 - c.stateProgress;
  } else if (c.visibilityState === VisibilityState.APPEARING) {
    c.opacity = c.stateProgress;
  }

  if (parentSizeChanged) {
    if (c.minLayerWidth === 0 || c.minLayerHeight === 0) {
      setCanvasLayerSize(c, 0, 0);
    }
    if (c.listMode) {
      requestLayerPosition(c, c.layer.pos);
    }
  }

  if (c.requestApplyFocus) {
    canvasApplyFocus(c, c.requestApplyFocus.focus || c.focus, c.requestApplyFocus.speed);
    c.requestApplyFocus = null;
  }

  if (c.scrollSliderHor && c.scrollSliderHor.height != c.height) {
    c.scrollSliderHor.height = c.height;
  }

  if (c.autoSize) {
    checkAllBounds(c);
  }

  if (c.mouseDownOnMe) {
    if (c.allowScrollVert || c.allowScrollHor) {
      if (buttonStates.LEFT_MOUSE.changedThisFrame) {
        c.layerDragOffset = subtractV2(c.layerBasePos, m.mousePos);
      }

      const newPos = addV2(m.mousePos, c.layerDragOffset);

      if (!c.allowScrollHor) {
        newPos.x = c.layerBasePos.x;
      }
      if (!c.allowScrollVert) {
        newPos.y = c.layerBasePos.y;
      }

      c.stickToDefaultFocus = false;

      requestLayerPosition(c, newPos, false);
    }
  }

  if (c.zoomSlider) {
    if (c.zoomSlider.valueSetByUserThisFrame || c.zoomSlider.knobIsDragged) {
      const value = 100 - c.zoomSlider.valuePercentage * 100;
      setNormalizedZoomLevel(c, value, null);
      c.zoomSlider.infoText = `zoom ${Math.floor(c.layer.scale * 100)}%`;
    }
  }

  if (c.allowZoom && c.normalizedZoomLevelTarget) {
    if (c.normalizedZoomLevelTarget !== c.normalizedZoomLevel) {
      const diff = c.normalizedZoomLevel - c.normalizedZoomLevelTarget;

      const zoomSpeed = Math.min(diff, Math.sign(diff) * 50);
      const zoomValue = zoomSpeed * dt;

      zoomCanvas(c, zoomSpeed * dt);
    }
  }

  if (c.refocus && c.refocus.btn.clickedThisFrame) {
    hideToolTip(c.refocus.container, true, true);
    canvasRefocus(c);
  }

  if (c.scrollAnimation) {
    const ignoreBorders = c.scrollAnimation.end.x === 0 && c.scrollAnimation.end.y === 0;
    requestLayerPosition(c, c.scrollAnimation.currentValue, true, ignoreBorders);

    if (c.scrollAnimation.p >= 1) {
      c.scrollAnimation.doKill = true;
      c.scrollAnimation = null;
    }
  } else {
    if (isArrowKeyDown()) {
      if (
        getObjectCountByType(c.manager, ObjectType.TEXT_EDITOR) === 0 &&
        getObjectCountByType(c.manager, ObjectType.USER_TEXT_EDITOR) === 0 &&
        c.manager.cursor.allowTyping
      ) {
        let newPos = cloneV2(c.layerBasePos);
        if (c.allowScrollHor && buttonStates.LEFT.isDown) {
          newPos = addV2(newPos, v2(10, 0));
        }
        if (c.allowScrollHor && buttonStates.RIGHT.isDown) {
          newPos = addV2(newPos, v2(-10, 0));
        }
        if (c.allowScrollVert && buttonStates.UP.isDown) {
          newPos = addV2(newPos, v2(0, 10));
        }
        if (c.allowScrollVert && buttonStates.DOWN.isDown) {
          newPos = addV2(newPos, v2(0, -10));
        }

        requestLayerPosition(c, newPos, true);
      }
    }
  }
  if (c.zoomAnimation) {
    c.normalizedZoomLevel = getNormalizedZoomLevel(c.zoomAnimation.currentValue);
    setScale(c, c.zoomAnimation.currentValue);
    if (c.zoomAnimation.p >= 1) {
      c.zoomAnimation.doKill = true;
      c.zoomAnimation = null;
    }
  }

  if (c.doCheckFocus) {
    const isAutoMoving = !!c.scrollAnimation || !!c.zoomAnimation;
    const canvasMovedThisFrame = c.canvasMovedThisFrame;
    c.canvasMovedThisFrame = false;

    if (isAutoMoving) {
      c.focusCheckCooldown = FOCUS_CHECK_COOLDOWN;
      if (c.refocus) {
        setVisibilityState(c.refocus.container, VisibilityState.DISAPPEARING);
        c.refocus = null;
        c.isOutOfFocus = false;
      }
    } else if (canvasMovedThisFrame) {
      c.focusCheckCooldown = FOCUS_CHECK_COOLDOWN;
      if (c.refocus) {
        checkFocusPosition(c);
      }
    } else if (c.focusCheckCooldown > 0) {
      c.focusCheckCooldown = Math.max(0, c.focusCheckCooldown - dt);
      if (c.focusCheckCooldown === 0) {
        checkFocusPosition(c);
      }
    } else {
      checkFocusPosition(c);
    }
  }

  return false;
}

export function requestCanvasLayerTranslate(c: Canvas, translate: V2) {
  const newPos = addV2(c.layerBasePos, translate);

  const diff = requestLayerPosition(c, newPos, false);
}

function requestLayerPosition(
  c: Canvas,
  newPos: V2,
  moveLinkedCanvas = true,
  ignoreBorders = false,
): V2 {
  let yRatio = 0.5;

  if (!ignoreBorders && !c.noBorders) {
    const yContentDoesNotFit = c.layer.height > c.height;
    const isYRecenter = newPos.y === 0;

    if (!yContentDoesNotFit && c.listMode) {
      newPos.y = 0;
    }

    if (c.allowZoom || yContentDoesNotFit || isYRecenter || c.listMode) {
      const halfHeight = c.height / 2;
      if (c.listMode) {
        if (c.layer.height < c.height) {
          newPos.y = -c.topBound * c.layer.scale - halfHeight;
        } else {
          if (newPos.y > -c.topBound * c.layer.scale - halfHeight) {
            newPos.y = -c.topBound * c.layer.scale - halfHeight;
          } else if (newPos.y < -c.bottomBound * c.layer.scale + halfHeight) {
            newPos.y = -c.bottomBound * c.layer.scale + halfHeight;
          }
        }
      } else {
        if (newPos.y > -c.topBound * c.layer.scale + halfHeight) {
          newPos.y = -c.topBound * c.layer.scale + halfHeight;
        } else if (newPos.y < -c.bottomBound * c.layer.scale - halfHeight) {
          newPos.y = -c.bottomBound * c.layer.scale - halfHeight;
        }
      }
    } else {
      newPos.y = c.layerBasePos.y;
    }

    const xContentDoesNotFit = c.layer.width > c.width;
    const isXRecenter = newPos.x === 0;
    if (c.allowZoom || xContentDoesNotFit || isXRecenter) {
      const halfWidth = c.width / 2;
      if (newPos.x > -c.leftBound * c.layer.scale + halfWidth) {
        newPos.x = -c.leftBound * c.layer.scale + halfWidth;
      } else if (newPos.x < -c.rightBound * c.layer.scale - halfWidth) {
        newPos.x = -c.rightBound * c.layer.scale - halfWidth;
      }
    } else {
      newPos.x = c.layerBasePos.x;
    }
  }

  if (c.scrollSliderHor) {
    setSliderValue(c.scrollSliderHor, yRatio, true);
  }

  const diff = subtractV2(newPos, c.layerBasePos);
  c.layerBasePos = newPos;

  setCanvasLayerPos(c, c.layerBasePos, moveLinkedCanvas);

  return diff;
}

export function setCanvasLayerPos(c: Canvas, newPos: V2, moveLinkedCanvas = true) {
  c.focusPosition.x = -newPos.x / c.layer.scale;
  c.focusPosition.y = -newPos.y / c.layer.scale;
  c.canvasMovedThisFrame = true;

  const diff = subtractV2(newPos, c.layer.pos);

  setEntityPosition(c.layer, newPos.x, newPos.y);

  if (c.linked) {
    setEntityPosition(c.linked, newPos.x, newPos.y);
  }
}

export function setCanvasFocusPositionAndScale(
  canvas: Canvas,
  fp: V2,
  duration: number = 1,
  ignoreBorders = false,
  targetScale: number = 0,
) {
  canvas.focusPosition = fp;


  const userScale = targetScale || canvas.layer.scale;

  const scrollTarget = v2(-fp.x * userScale, -fp.y * userScale);

  if (duration === 0) {
    requestLayerPosition(canvas, scrollTarget, true, ignoreBorders);
    if (targetScale) {
      setScale(canvas, targetScale);
    }
  } else {
    canvas.scrollAnimation = {
      doKill: false,
      type: AnimationType.POSITION,
      delay: 0,
      start: cloneV2(canvas.layer.pos),
      end: scrollTarget,
      duration,
      easeFunction: Ease.inOutQuad,
      t: 0,
      p: 0,
      pEased: 0,
      currentValue: cloneV2(canvas.layer.pos),
      lerpFunction: lerpV2,
      prop: '',
    };

    canvas.manager.animations.push(canvas.scrollAnimation);

    if (targetScale) {
      canvas.zoomAnimation = {
        doKill: false,
        type: AnimationType.SCALE,
        delay: 0,
        start: canvas.layer.scale,
        end: targetScale,
        duration,
        easeFunction: Ease.inOutQuad,
        t: 0,
        p: 0,
        pEased: 0,
        currentValue: canvas.layer.scale,
        lerpFunction: lerpNumber,
        prop: '',
      };
      canvas.manager.animations.push(canvas.zoomAnimation);
    }
  }
}

export function setNormalizedZoomLevelTarget(canvas: Canvas, target: number) {
  canvas.normalizedZoomLevelTarget = target;
}

export function zoomCanvas(canvas: Canvas, zoom: number, focus: V2 = null) {
  canvas.stickToDefaultFocus = false;
  setNormalizedZoomLevel(canvas, canvas.normalizedZoomLevel - zoom, focus);
}

export function scrollCanvas(canvas: Canvas, diff: V2) {
  canvas.stickToDefaultFocus = false;
  const hor = canvas.allowScrollHor ? diff.x : 0;
  const vert = canvas.allowScrollVert ? diff.y : 0;
  requestCanvasLayerTranslate(canvas, v2(hor, vert));
}

export function setDefaultZoom(canvas: Canvas) {
  const newScale = 1;

  const ANIM_DURATION_SEC = 0.2;

  canvas.stickToDefaultFocus = false;

  setCanvasFocusPositionAndScale(canvas, canvas.focusPosition, ANIM_DURATION_SEC, false, newScale);
}

export function setNormalizedZoomLevel(
  canvas: Canvas,
  zoomLevel: number,
  focusPosition: V2 = null,
) {
  if (!focusPosition) {
    // focusPosition = space.center;
    focusPosition = v2();
  }

  if (!canvas.allowScrollHor) {
    focusPosition.x = canvas.manager.space.width / 2;
  }

  const clampedLevel = clamp(zoomLevel, 0, 100);
  const newScale = getScale(clampedLevel);

  canvas.normalizedZoomLevel = clampedLevel;

  scaleWhileKeepingFocus(canvas, newScale, focusPosition);
}

export function scaleWhileKeepingFocus(canvas: Canvas, scale: number, focus: V2) {
  const oldScale = canvas.layer.scale;

  const scale_factor = scale / oldScale;

  const mouseX = focus.x - getAbsolutePos(canvas.centerAnchor).x;
  const mouseY = focus.y - getAbsolutePos(canvas.centerAnchor).y;

  const diffX = focus.x - getAbsolutePos(canvas.layer).x;
  const diffY = focus.y - getAbsolutePos(canvas.layer).y;

  let new_coordinate_x = mouseX - diffX * scale_factor;
  let new_coordinate_y = mouseY - diffY * scale_factor;

  const newPos = v2(new_coordinate_x, new_coordinate_y);

  setScale(canvas, scale, false);
  requestLayerPosition(canvas, newPos, false);
}

function attachChild(child: GameObject, parent: Canvas) {
  {
    setParent(child, parent.layer);
  }

  //handle zIndex of statc & dynamic objects
  if (child.allowDrag) {
  } else {
    //TODO: other user cursors should be on a specific layer, and not scale
    if (child.objectType !== ObjectType.OTHER_CLIENT) {
      moveEntityToBack(child);
    }
  }

  if (parent.autoSize) {
    checkBoundsForChild(parent, child);
    setCenterContainer(parent);
  }
}

export function setCanvasLayerSize(canvas: Canvas, width: number = 0, height: number = 0) {
  canvas.minLayerWidth = width;
  canvas.minLayerHeight = height;
  canvas.layer.width = Math.max(width || canvas.width, 10);
  canvas.layer.height = Math.max(height || canvas.height, 10);

  canvas.layer.layoutChanged = true;
  canvas.leftBound = -canvas.layer.width / 2;
  canvas.rightBound = canvas.layer.width / 2;
  canvas.topBound = -canvas.layer.height / 2;
  canvas.bottomBound = canvas.layer.height / 2;

  if (canvas.listMode) {
    requestLayerPosition(canvas, canvas.layerBasePos, false, false);
  }

  setCenterContainer(canvas);
}

function setCenterContainer(canvas: Canvas) {
  const w = -canvas.leftBound + canvas.rightBound;
  const h = -canvas.topBound + canvas.bottomBound;
  const xRato = w === 0 ? 0 : -canvas.leftBound / w;
  const xRatoDiff = xRato - 0.5;
  const x = -w * xRatoDiff;

  const yRato = h === 0 ? 0 : -canvas.topBound / h;
  const yRatoDiff = yRato - 0.5;
  const y = -h * yRatoDiff;

  canvas.centerContainer.width = w;
  canvas.centerContainer.height = h;
  setEntityPosition(canvas.centerContainer, x, y);
}

function checkAllBounds(canvas: Canvas) {
  canvas.leftBound = -canvas.minLayerWidth / 2;
  canvas.rightBound = canvas.minLayerWidth / 2;
  canvas.topBound = -canvas.minLayerHeight / 2;
  canvas.bottomBound = canvas.minLayerHeight / 2;

  for (let i = canvas.layer.children.length - 1; i >= 0; i--) {
    if (canvas.layer.children[i] === canvas.centerContainer) continue;
    checkBoundsForChild(canvas, canvas.layer.children[i]);
  }

  setCenterContainer(canvas);
}

function checkBoundsForChild(canvas: Canvas, child: GameObject) {
  const halfWidth = child.shape === Shape.CIRCLE ? child.radius : child.width / 2;
  const halfHeight = child.shape === Shape.CIRCLE ? child.radius : child.height / 2;
  const PADDING = 100;
  const leftBound = Math.min(child.pos.x - halfWidth - PADDING, -canvas.minLayerWidth / 2);
  const rightBound = Math.max(child.pos.x + halfWidth + PADDING, canvas.minLayerWidth / 2);
  const topBound = Math.min(child.pos.y - halfHeight - PADDING, -canvas.minLayerHeight / 2);
  const bottomBound = Math.max(child.pos.y + halfHeight + PADDING, canvas.minLayerHeight / 2);

  if (leftBound < 0 && leftBound < canvas.leftBound) {
    canvas.leftBound = leftBound;
  }
  if (rightBound > 0 && rightBound > canvas.rightBound) {
    canvas.rightBound = rightBound;
  }
  if (topBound < 0 && topBound < canvas.topBound) {
    canvas.topBound = topBound;
  }
  if (bottomBound > 0 && bottomBound > canvas.bottomBound) {
    canvas.bottomBound = bottomBound;
  }
}

function getNormalizedZoomLevel(scale: number): number {
  // return ZOOM_CONSTANT_A * Math.exp(ZOOM_CONSTANT_B * value);

  // Scale = A * exp(B * n);
  // n = Math.log(scale / A) / B;
  return Math.log(scale / ZOOM_CONSTANT_A) / ZOOM_CONSTANT_B;
}

export function getScale(value: number): number {
  return ZOOM_CONSTANT_A * Math.exp(ZOOM_CONSTANT_B * value);

  //What is this forumla?
  //To provide constant ratio with constant argument difference, you need exponential function (it is possible to use any base, e, 2, 10 and so on with corresponding logarithms)

  // F(x) = A * Exp(B * x)
  // To get coefficients A and B for given border conditions (argument x0 corresponds to function value F0):

  // F0 = A * Exp(B * x0)
  // F1 = A * Exp(B * x1)
  // divide the second equation by the first:

  // Exp(B * (x1 -x0) = F1 / F0
  // B * (x1 -x0) = ln(F1 / F0)
  // so

  // B = ln(F1 / F0) / (x1 - x0)
  // and

  // A = F0 * Exp(-B * x0)
  // For your example

  // x0=0, x1=100 (which zoom scale do you want to use? in this case 0 to 100)
  // zoom0 = 0.2, zoom1=1 (minimum and maximum scale percentage. In this case 20% to 100%)
  // B = ln(5) / 100 = 0.0161
  // A = 0.2 * Exp(0) = 0.2
  // zoom(i) = 0.2 * Exp(0.0161 * i)

  // zoom(0) = 0.2
  // zoom(50) = 0.447
  // zoom(100) = 1

  // note that
  // zoom(50) / zoom(0) = zoom(100) / zoom(50)
}

function createScrollSliderForCanvas(canvas: Canvas) {
  {
    const slider = createAndAddSlider(canvas.manager, '', SliderOrientation.VERTICAL);
    setParent(slider, canvas);
    slider.knob.radius = 10;
    slider.maxBarWidth = 5;
    slider.barColorBack = COLORS.TRANSPARENT;
    slider.barColorValue = COLORS.TRANSPARENT;
    slider.height = canvas.height;
    slider.width = 5;
    slider.anchoredPos = anchoredPos(v2(1, 0.5), v2(0, 0), v2(1, 0.5));
    slider.passHover = true;
    slider.moveMode = MoveMode.ANCHORED;
    slider.min = 0;
    slider.max = 100;
    canvas.scrollSliderHor = slider;
    setVisibilityState(slider, VisibilityState.APPEARING);
  }
}

function setAutoZoom(canvas: Canvas) {
  const h = canvas.manager.space.height;
  const w = canvas.manager.space.width;
  const zoomLevelVert = remapNumber(h, 330, 760, 25, NORMALIZED_ZOOM_100, true);
  const zoomLevelHor = remapNumber(w, 500, 1700, 25, NORMALIZED_ZOOM_100, true);
  const startLevel = Math.min(zoomLevelVert, zoomLevelHor);
  setNormalizedZoomLevel(canvas, startLevel);
}

function setFocus(canvas: Canvas, x: number, y: number, w: number, h: number, speed: CANVAS_SPEED) {
  canvas.focus.positionX = x;
  canvas.focus.positionY = y;
  canvas.focus.width = w;
  canvas.focus.height = h;
  canvas.focus.speed = speed;

  canvas.stickToDefaultFocus = true;

  canvas.focusPosition = v2(canvas.focus.positionX, canvas.focus.positionY);

  if (canvas.debugFocusContainer) {
    setEntityPosition(canvas.debugFocusContainer, canvas.focus.positionX, canvas.focus.positionY);
    canvas.debugFocusContainer.width = w;
    canvas.debugFocusContainer.height = h;
  }

  canvas.requestApplyFocus = { focus: canvas.focus, speed };
}

export function canvasApplyFocus(canvas: Canvas, focus: CanvasFocus, speed: CANVAS_SPEED) {
  let fp = v2(focus.positionX, focus.positionY - 40);

  if (canvas.relativeScreenFocusPoint.x !== 0.5 || canvas.relativeScreenFocusPoint.y !== 0.5) {
    const pointX = canvas.relativeScreenFocusPoint.x;
    const pointY = canvas.relativeScreenFocusPoint.y;
    const xOffset = (1 - pointX - 0.5) * canvas.width;
    const yOffset = (1 - pointY - 0.5) * canvas.height;
    const scaledOffset = v2(xOffset / canvas.layer.scale, yOffset / canvas.layer.scale);

    fp = addV2(fp, scaledOffset);
  }

  const onInit = !canvas.id || canvas.lifeTime <= 0.2;

  const duration = onInit ? 0 : DURATION_PER_SPEED[speed];
  const ignoreBorders = true;

  const zoomW = (canvas.width || 1000) / (canvas.focus.width || 1000);
  const zoomH = ((canvas.height || 700) - 75) / (canvas.focus.height || 700);

  const smallestScale = clamp(Math.min(zoomW, zoomH), minZoomLevel, maxZoomLevel);

  setCanvasFocusPositionAndScale(canvas, fp, duration, ignoreBorders, smallestScale);
}

function setScale(canvas: Canvas, scale: number, scaleLinkedCanvas = true) {
  canvas.layer.scale = scale;

  canvas.focusSize.width = canvas.width / canvas.layer.scale;
  canvas.focusSize.height = canvas.height / canvas.layer.scale;

  canvas.normalizedZoomLevel;

  // if (scaleLinkedCanvas && canvas.linkedCanvas) {
  //   setScale(canvas.linkedCanvas, scale, false);
  // }

  if (canvas.linked) {
    canvas.linked.scale = scale;
  }

  if (canvas.onScaleChange) {
    canvas.onScaleChange(canvas, scale);
  }
}

export function canvasZoomIn(canvas: Canvas) {
  const scale = canvas.layer.scale;
  const newScale = getNextScaleStep(SCALE_STEPS, scale);

  const ANIM_DURATION_SEC = 0.2;

  canvas.stickToDefaultFocus = false;

  setCanvasFocusPositionAndScale(canvas, canvas.focusPosition, ANIM_DURATION_SEC, false, newScale);
}

export function canvasZoomOut(canvas: Canvas) {
  const scale = canvas.layer.scale;
  const newScale = getPreviousScaleStep(SCALE_STEPS, scale);
  const ANIM_DURATION_SEC = 0.2;

  canvas.stickToDefaultFocus = false;

  setCanvasFocusPositionAndScale(canvas, canvas.focusPosition, ANIM_DURATION_SEC, false, newScale);
}

function getPreviousScaleStep(steps: number[], value: number): number {
  if (steps.length === 0) return value;
  if (steps.length === 1) return steps[0];
  if (value < steps[0]) return steps[0];

  for (let i = 0; i < steps.length - 1; i++) {
    if (value <= steps[i + 1]) {
      return steps[i];
    }
  }

  return steps[steps.length - 1];
}

function getNextScaleStep(steps: number[], value: number): number {
  if (steps.length === 0) return value;
  if (steps.length === 1) return steps[0];
  if (value > steps[steps.length - 1]) return steps[steps.length - 1];

  for (let i = steps.length - 1; i > 0; i--) {
    if (value >= steps[i - 1]) {
      return steps[i];
    }
  }

  return steps[0];
}

function checkFocusPosition(c: Canvas) {
  if (c.stickToDefaultFocus) {
    if (c.refocus) {
      setVisibilityState(c.refocus.container, VisibilityState.DISAPPEARING);
      c.refocus = null;
    }
    c.isOutOfFocus = false;
    return;
  }

  const diffX = Math.abs(c.focus.positionX - c.focusPosition.x);
  const diffY = Math.abs(c.focus.positionY - c.focusPosition.y);

  const zoomW = c.width / c.focus.width;
  const zoomH = c.height / c.focus.height;

  const smallestScale = clamp(Math.min(zoomW, zoomH), minZoomLevel, maxZoomLevel);
  const scaleDiff = (smallestScale - c.layer.scale) * 100;

  const isOutOfFocus =
    !c.scrollAnimation &&
    !c.zoomAnimation &&
    hasVisibleState(c.visibilityState) &&
    (diffX > c.focus.width / 2 || diffY > c.focus.height / 2 || scaleDiff < -32 || scaleDiff > 15);

  if (isOutOfFocus !== c.isOutOfFocus) {
    c.isOutOfFocus = isOutOfFocus;

    if (isOutOfFocus) {
      showRecenter(c);
      const anchor = getReturnButtonAnchor(c);
      if (anchor) {
        if (c.showNavigationTip) {
          startCanvasNavigationTutorial(c);
        } else if (c.showRefocusTip) {
          startCanvasRefocusTip(c, anchor);
        }
      }
    } else if (c.refocus) {
      setVisibilityState(c.refocus.container, VisibilityState.DISAPPEARING);
      c.refocus = null;
    }
  }
}

export function createPackage(canvas: Canvas, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (canvas.doClipChildren) {
    {
      let rect: RenderPackageRect = {
        type: RenderItemType.rect,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x,
        positionY: position.y,
        scale,
        width: canvas.width,
        height: canvas.height,
        shapeStyle: SHAPE_STYLES.NONE,
        cornerRadius1: 0,
        cornerRadius2: 0,
        cornerRadius3: 0,
        cornerRadius4: 0,
        shadow: null,
      };
      rPackage.push(rect);
    }
  }
  if (canvas.debugView) {
    {
      let rect: RenderPackageRect = {
        type: RenderItemType.rect,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x,
        positionY: position.y,
        scale,
        width: canvas.width,
        height: canvas.height,
        shapeStyle: SHAPE_STYLES.DEBUG_GREEN,
        cornerRadius1: 0,
        cornerRadius2: 0,
        cornerRadius3: 0,
        cornerRadius4: 0,
        shadow: null,
      };
      rPackage.push(rect);
    }
  }

  return rPackage;
}

//sets canvas to saved focus
export function canvasRefocus(c: Canvas) {
  // c.stickToDefaultFocus = true;
  c.requestApplyFocus = { focus: c.focus, speed: CANVAS_SPEED.FAST };
}

// The entity the refocus tooltip should point at: the canvas DOM button when present.
function getReturnButtonAnchor(c: Canvas): GameObject | null {
  if (c.refocus) {
    return c.refocus.container;
  }
  return null;
}

function showRecenter(canvas: Canvas) {
  const c = createAndAddLocalContainer(canvas.manager, getZeroAnchoredEntity(), {
    ...SHAPE_STYLES.WHITE,
    shadow: SHADOWS.DEFAULT,
  });
  c.width = 120;
  c.height = 120;
  c.cornerRadius1 = 50;
  c.cornerRadius2 = 50;
  c.cornerRadius3 = 50;
  c.cornerRadius4 = 50;
  c.anchoredPos = anchoredPos(v2(0.5, 0.6), v2(0, 0), v2(0.5, 0.5));

  const style = BUTTON_STYLES.CANVAS_RECENTER;
  const btn = createAndAddLocalIconButton(canvas.manager, style);
  btn.moveMode = MoveMode.ANCHORED;
  btn.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
  btn.label = t('canvas.return_to_step');
  btn.labelSide = Side.ABOVE;
  setParent(btn, c);

  const props: Partial<TextObject2> = {
    text: t('canvas.refocus_step'),
    width: 220,
    defaultTextStyle: {
      color: COLORS.BLACK_100,
      fontSize: 16,
      fontId: 'font_regular',
      lineHeight: 1,
    },
  };

  createTransformAnchoredAnimation(canvas.manager, c, {
    end: anchoredPos(v2(0.5, 0.8), v2(0, 0), v2(0.5, 0.5)),
    delay: 0.3,
    duration: 0.4,
    easeFunction: Ease.outQuad,
  });

  setParent(c, canvas);

  canvas.refocus = { btn, container: c };
}

function startCanvasNavigationTutorial(canvas: Canvas) {
  const text = [
    '<b>Navigation</b>\nUse the mission canvas to navigate when you move away from the current step.',
    '<b>Move around</b>\nSlide with 2 fingers across the trackpad.\nYou can also click and drag the mission background.',
    '<b>Zoom in and out</b>\nPinch with 2 fingers to zoom in and out on your trackpad.\nUse CTRL + scroll to zoom with your mouse.',
  ];
  showStickyToolTip(
    canvas,
    text,
    'navigation_tip',
    anchoredPos(v2(1, 0.5), v2(-30, 0), v2(1, 0.5)),
    Side.NONE,
    0,
    toSize(280, 220),
    () => {
      canvas.showNavigationTip = false;
      const anchor = getReturnButtonAnchor(canvas);
      if (anchor) {
        startCanvasRefocusTip(canvas, anchor);
      }
    },
    true,
  );
}

function startCanvasRefocusTip(canvas: Canvas, anchor: GameObject) {
  const text = [
    '<b>Return to the current step</b>\nClick here to quickly return to the current step.',
  ];

  // The canvas DOM button sits mid-screen (tip below it); the step controller circle
  // sits at the bottom edge, so its tip must point upward to avoid clipping.
  const isCanvasButton = canvas.refocus !== null && anchor === canvas.refocus.container;

  let side = Side.ABOVE;
  let anchorPoint = anchoredPos(v2(0.5, 1), v2(0, 16), v2(0.5, 0));
  if (isCanvasButton) {
    side = Side.BELOW;
    anchorPoint = anchoredPos(v2(0.5, 0), v2(0, -16), v2(0.5, 1));
  }

  showStickyToolTip(anchor, text, 'refocus_tip', anchorPoint, side, 0, toSize(280, 220), () => {
    canvas.showRefocusTip = false;
  });
}
